<script setup>
import { ref, onMounted, computed } from 'vue'
import { get_payments, downloadInvoice as downloadInvoiceService } from '@/services/payments';
import { formattedDate, formatCurrency, swalError } from '@/helper';

const payments = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const perPage = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);

// Filter and sort
const searchQuery = ref('');
const statusFilter = ref('');
const sortBy = ref('created_at');
const sortOrder = ref('desc');

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'refunded', label: 'Refunded' }
];

const sortOptions = [
  { value: 'created_at', label: 'Date Created' },
  { value: 'amount', label: 'Amount' },
  { value: 'status', label: 'Status' },
  { value: 'child_name', label: 'Child Name' }
];

// Fetch payments
const fetchPayments = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: perPage.value,
      status: statusFilter.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value
    };

    // Add search query if present
    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    const response = await get_payments(params);

    if (!response.error) {
      payments.value = response.data.payments || [];
      totalCount.value = response.data.pagination?.total_count || 0;
      totalPages.value = response.data.pagination?.total_pages || 0;
    } else {
      console.error('Error fetching payments:', response.message);
    }
  } catch (error) {
    console.error('Error fetching payments:', error);
  } finally {
    loading.value = false;
  }
};

// Get status badge class
const getStatusClass = (status) => {
  const statusMap = {
    pending: 'badge bg-warning',
    processing: 'badge bg-info',
    succeeded: 'badge bg-success',
    failed: 'badge bg-danger',
    canceled: 'badge bg-secondary',
    refunded: 'badge bg-light text-dark'
  };
  return statusMap[status] || 'badge bg-secondary';
};

// Filtered payments based on search
const filteredPayments = computed(() => {
  return payments.value.filter(payment => {
    if (!searchQuery.value) return true;
    const query = searchQuery.value.toLowerCase();
    return (
      payment.invoice_number?.toLowerCase().includes(query) ||
      payment.child_name?.toLowerCase().includes(query) ||
      payment.user_name?.toLowerCase().includes(query) ||
      payment.package_name?.toLowerCase().includes(query)
    );
  });
});

// Pagination helpers
const onPageChange = (page) => {
  currentPage.value = page;
  fetchPayments();
};

const onPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    fetchPayments();
  }
};

const onNextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    fetchPayments();
  }
};

const goToFirstPage = () => {
  currentPage.value = 1;
  fetchPayments();
};

const goToLastPage = () => {
  currentPage.value = totalPages.value;
  fetchPayments();
};

// Handle filter changes
const applyFilters = () => {
  currentPage.value = 1;
  fetchPayments();
};

const resetFilters = () => {
  searchQuery.value = '';
  statusFilter.value = '';
  sortBy.value = 'created_at';
  sortOrder.value = 'desc';
  currentPage.value = 1;
  fetchPayments();
};

// Calculate statistics
const stats = computed(() => {
  const totalAmount = payments.value.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const succeededCount = payments.value.filter(p => p.status === 'succeeded').length;
  const pendingCount = payments.value.filter(p => p.status === 'pending').length;
  const failedCount = payments.value.filter(p => p.status === 'failed').length;

  return {
    totalAmount,
    succeededCount,
    pendingCount,
    failedCount
  };
});

async function downloadInvoice(paymentId) {
  const response = await downloadInvoiceService(paymentId);
  
  if (response.error) {
    swalError('Failed to download invoice.');
    return;
  }
  
  const blob = new Blob([response.data.data], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  
  // Extract filename from Content-Disposition header
  const headers = response.data.headers;
  const contentDisposition = headers['content-disposition'] || 
                             headers['Content-Disposition'] || 
                             headers.get?.('content-disposition') ||
                             headers.get?.('Content-Disposition');
  
  let filename = 'invoice.pdf';
  
  if (contentDisposition) {
    // Try to match filename="xxx" or filename=xxx
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }
  
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Initialize
onMounted(() => {
  fetchPayments();
});
</script>

<template>
  <div class="container-fluid">
    
    <Breadcrumbs main="Payments" title="Payment History" />

    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-md-3 mb-3">
        <div class="card">
          <div class="card-body">
            <h6 class="text-muted mb-2">Total Payments</h6>
            <h3 class="mb-0">{{ totalCount }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="card">
          <div class="card-body">
            <h6 class="text-muted mb-2">Total Amount</h6>
            <h3 class="mb-0">{{ formatCurrency(stats.totalAmount) }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="card">
          <div class="card-body">
            <h6 class="text-muted mb-2">Succeeded</h6>
            <h3 class="mb-0 text-success">{{ stats.succeededCount }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="card">
          <div class="card-body">
            <h6 class="text-muted mb-2">Pending</h6>
            <h3 class="mb-0 text-warning">{{ stats.pendingCount }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters
    <div class="card mb-4">
      <div class="card-body">
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label">Search</label>
            <input 
              v-model="searchQuery" 
              type="text" 
              class="form-control" 
              placeholder="Search by invoice, child name, parent name..."
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Status</label>
            <select v-model="statusFilter" class="form-select">
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Sort By</label>
            <select v-model="sortBy" class="form-select">
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="col-md-2 mb-3">
            <label class="form-label">Sort Order</label>
            <select v-model="sortOrder" class="form-select">
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <button @click="applyFilters" class="btn btn-primary me-2">
              <i class="feather icon-filter"></i> Apply Filters
            </button>
            <button @click="resetFilters" class="btn btn-secondary">
              <i class="feather icon-x"></i> Reset
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payments Table -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">Payments List</h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <div v-else-if="filteredPayments.length === 0" class="alert alert-info">
          No payments found.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-striped table-hover">
            <thead class="bg-light">
              <tr>
                <th>Invoice #</th>
                <th>Parent Name</th>
                <th>Child Name</th>
                <th>Package</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in filteredPayments" :key="payment.id">
                <td>
                  <strong>{{ payment.invoice_number || 'N/A' }}</strong>
                </td>
                <td>{{ payment.user_name || 'N/A' }}</td>
                <td>{{ payment.child_name }}</td>
                <td>{{ payment.package_name }}</td>
                <td>
                  <strong>{{ formatCurrency(payment.amount, payment.currency) }}</strong>
                </td>
                <td>
                  <span :class="getStatusClass(payment.status)">
                    {{ payment.status }}
                  </span>
                </td>
                <td class="text-muted small">
                  {{ formattedDate(payment.created_at) }}
                </td>
                <td>
                  <div class="btn-group btn-group-sm" role="group">
                    <button 
                      type="button" 
                      class="btn btn-light-primary"
                      title="Download"
                      @click="downloadInvoice(payment.id)"
                    >
                      <i class="feather icon-download"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1 && !loading" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalCount) }} of {{ totalCount }} payments
          </div>
          <nav aria-label="Page navigation">
            <ul class="pagination mb-0">
              <li :class="['page-item', { disabled: currentPage === 1 }]">
                <button @click="goToFirstPage" class="page-link">First</button>
              </li>
              <li :class="['page-item', { disabled: currentPage === 1 }]">
                <button @click="onPreviousPage" class="page-link">Previous</button>
              </li>
              <li v-for="page in Math.min(5, totalPages)" :key="page" :class="['page-item', { active: page === currentPage }]">
                <button @click="onPageChange(page)" class="page-link">{{ page }}</button>
              </li>
              <li :class="['page-item', { disabled: currentPage === totalPages }]">
                <button @click="onNextPage" class="page-link">Next</button>
              </li>
              <li :class="['page-item', { disabled: currentPage === totalPages }]">
                <button @click="goToLastPage" class="page-link">Last</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.table-responsive {
  border-radius: 0.25rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.pagination {
  gap: 5px;
}

.page-link {
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.page-link:hover:not(.disabled) {
  background-color: #e9ecef;
}

.page-item.active .page-link {
  background-color: #41b882;
  border-color: #41b882;
}
</style>